import type { RadarFrame } from "@divespots/contracts";

export type FrameState =
  { status: "loading" } | { status: "ready"; frame: RadarFrame } | { status: "error"; at: number };

type Fetcher = (timeMs: number, signal: AbortSignal) => Promise<RadarFrame>;

/** A failed frame is asked for again on selection only after this long. */
export const FRAME_RETRY_MS = 15_000;

/**
 * Fetches the frames of the index: the selected one at once, the others in the background one
 * at a time, newest first. A failed frame stays failed (it never turns into another frame).
 */
export class RadarFrameStore {
  private states = new Map<number, FrameState>();
  private controllers = new Map<number, AbortController>();
  private times: number[] = [];
  private wanted: number[] = [];
  private background: number | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  private disposed = false;

  constructor(
    private readonly fetchFrame: Fetcher,
    private readonly nowFn: () => number = Date.now,
  ) {}

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => void this.listeners.delete(cb);
  };

  getVersion = (): number => this.version;

  get(timeMs: number): FrameState | undefined {
    return this.states.get(timeMs);
  }

  /** True once the frame has an answer (a picture or a failure). */
  settled(timeMs: number): boolean {
    const s = this.states.get(timeMs);
    return s !== undefined && s.status !== "loading";
  }

  /**
   * The index (ascending) and the frame times wanted now changed: the one on show and, while
   * playing, the next one (a single time, or null for none).
   */
  sync(times: readonly number[], want: number | null | readonly number[]): void {
    this.disposed = false;
    this.times = [...times];
    this.wanted = want === null ? [] : typeof want === "number" ? [want] : [...want];
    const keep = new Set(times);
    for (const t of [...this.states.keys()]) {
      if (keep.has(t)) continue;
      this.controllers.get(t)?.abort();
      this.controllers.delete(t);
      this.states.delete(t);
      if (this.background === t) this.background = null;
    }
    for (const w of this.wanted) {
      if (!keep.has(w)) continue;
      const s = this.states.get(w);
      if (!s || (s.status === "error" && this.nowFn() - s.at >= FRAME_RETRY_MS)) this.start(w);
    }
    this.pump();
  }

  dispose(): void {
    this.disposed = true;
    for (const c of this.controllers.values()) c.abort();
    this.controllers.clear();
    this.states.clear();
    this.background = null;
  }

  private pump(): void {
    if (this.background !== null) return;
    // the pictures wanted now come first: the background waits until they have an answer
    if (this.wanted.some((w) => this.states.get(w)?.status === "loading")) return;
    for (let k = this.times.length - 1; k >= 0; k--) {
      const t = this.times[k]!;
      if (this.states.has(t)) continue;
      this.background = t;
      this.start(t);
      return;
    }
  }

  private start(t: number): void {
    this.controllers.get(t)?.abort();
    const ctl = new AbortController();
    this.controllers.set(t, ctl);
    this.states.set(t, { status: "loading" });
    this.emit();
    this.fetchFrame(t, ctl.signal).then(
      (frame) => {
        if (ctl.signal.aborted || this.disposed) return;
        this.finish(t, ctl, { status: "ready", frame });
      },
      () => {
        if (ctl.signal.aborted || this.disposed) return;
        this.finish(t, ctl, { status: "error", at: this.nowFn() });
      },
    );
  }

  private finish(t: number, ctl: AbortController, state: FrameState): void {
    if (this.controllers.get(t) !== ctl) return;
    this.controllers.delete(t);
    this.states.set(t, state);
    if (this.background === t) this.background = null;
    this.emit();
    this.pump();
  }

  private emit(): void {
    this.version++;
    for (const l of this.listeners) l();
  }
}
